import axios from "axios";
import swal from "sweetalert";
import { format, isValid } from "date-fns";
import BASE_URL from "../assets/global/url";

// Returns true when a (non-deleted) cutoff period covers the given date.
// The date is sent as yyyy-MM-dd so the server's timezone can't shift it to
// the previous/next day.
export const hasCutoffForDate = async (date) => {
  const res = await axios.get(`${BASE_URL}/cutoff/dateValidation`, {
    params: { date: format(new Date(date), "yyyy-MM-dd") },
  });
  return res.data === true;
};

// Returns true when the cutoff covering the given date is already posted
// (closed). Same yyyy-MM-dd format as above.
export const isCutoffPostedForDate = async (date) => {
  const res = await axios.get(`${BASE_URL}/cutoff/validation/posted-cutoff`, {
    params: { date: format(new Date(date), "yyyy-MM-dd") },
  });
  return res.data === true;
};

// Full cutoff check for a selected date, shows the matching warning:
//   "posted"  -> the date is inside a cutoff that is already posted
//   "missing" -> no cutoff created yet for the date
//   "ok"      -> a cutoff exists and is still open (or the check couldn't run)
// Only warns - the caller decides what to do (e.g. revert the date when posted).
//
// Usage: const status = await checkCutoffDate(date, "Purchase Date");
export const checkCutoffDate = async (date, dateLabel = "Date") => {
  if (!date || !isValid(new Date(date))) return "ok";

  try {
    if (await isCutoffPostedForDate(date)) {
      await swal({
        icon: "warning",
        title: `Posted Cutoff for ${dateLabel}`,
        text: `A cutoff has already been posted for the selected ${dateLabel.toLowerCase()} (${format(new Date(date), "MMM/dd/yyyy")}). Please select a date within an open cutoff period.`, // prettier-ignore
      });
      return "posted";
    }
  } catch (error) {
    // Don't block the user if the check itself fails (network/server error)
    console.error(error);
    return "ok";
  }

  return (await warnIfNoCutoff(date, dateLabel)) ? "ok" : "missing";
};

// Shows a warning when no cutoff has been created yet for the selected date.
// Only warns - it does not clear the field, so the caller decides what to do
// with the result:
//   true  -> a cutoff exists (or the check couldn't run), nothing shown
//   false -> no cutoff yet, warning shown
//
// Usage: warnIfNoCutoff(date, "Invoice Date");
export const warnIfNoCutoff = async (date, dateLabel = "Date") => {
  if (!date || !isValid(new Date(date))) return true;

  try {
    if (await hasCutoffForDate(date)) return true;

    await swal({
      icon: "warning",
      title: `No Cutoff for ${dateLabel}`,
      text: `There is no cutoff created yet for the selected ${dateLabel.toLowerCase()} (${format(new Date(date), "MMM/dd/yyyy")}). Please create a cutoff for this period.`, // prettier-ignore
    });
    return false;
  } catch (error) {
    // Don't block the user if the check itself fails (network/server error)
    console.error(error);
    return true;
  }
};
